import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createCheckout, getCheckoutStatus } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Check, 
  Crown, 
  Loader2, 
  Star, 
  Zap, 
  MessageSquare, 
  TrendingUp, 
  Headphones,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('cardioai_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, userPlanRes] = await Promise.all([
          axios.get(`${API}/plans`),
          axios.get(`${API}/user/plan`, { headers: getAuthHeaders() })
        ]);
        setPlans(plansRes.data.plans);
        setUserPlan(userPlanRes.data);
      } catch (error) {
        console.error('Error fetching plans:', error);
        toast.error('Erro ao carregar planos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle payment return
  useEffect(() => {
    const payment = searchParams.get('payment');
    const plan = searchParams.get('plan');
    
    if (payment === 'success' && plan) {
      toast.success(`Parabéns! Agora és ${plan.toUpperCase()}!`);
      refreshUser && refreshUser();
    } else if (payment === 'cancelled') {
      toast.info('Pagamento cancelado');
    }
  }, [searchParams, refreshUser]);

  const handleUpgrade = async (planId) => {
    if (planId === 'free' || planId === userPlan?.plan) return;
    
    setProcessingPlan(planId);
    try {
      const response = await createCheckout(planId, window.location.origin);
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        toast.error('Erro ao criar sessão de pagamento');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento');
    } finally {
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'free': return <Zap className="w-8 h-8" />;
      case 'pro': return <Star className="w-8 h-8" />;
      case 'premium': return <Crown className="w-8 h-8" />;
      default: return <Zap className="w-8 h-8" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'free': return 'text-gray-400';
      case 'pro': return 'text-[#32CD32]';
      case 'premium': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getPlanBorder = (planId) => {
    switch (planId) {
      case 'free': return 'border-white/10';
      case 'pro': return 'border-[#32CD32]/50';
      case 'premium': return 'border-yellow-400/50';
      default: return 'border-white/10';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="pricing-page">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Escolhe o teu <span className="text-[#32CD32]">Plano</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Desbloqueia recursos avançados e leva o teu treino ao próximo nível
          </p>
          
          {userPlan && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#32CD32]/10 border border-[#32CD32]/30">
              <CheckCircle2 className="w-5 h-5 text-[#32CD32]" />
              <span className="text-[#32CD32] font-medium">
                Plano atual: {userPlan.plan_name}
              </span>
            </div>
          )}
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan) => {
            const isCurrentPlan = userPlan?.plan === plan.id;
            const isUpgrade = plan.id !== 'free' && userPlan?.plan === 'free';
            const isPremiumUpgrade = plan.id === 'premium' && userPlan?.plan === 'pro';
            
            return (
              <Card 
                key={plan.id}
                className={`bg-[#121212] ${getPlanBorder(plan.id)} relative overflow-hidden transition-all hover:scale-[1.02] ${
                  plan.id === 'pro' ? 'ring-2 ring-[#32CD32]/30' : ''
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.id === 'pro' && (
                  <div className="absolute top-0 right-0 bg-[#32CD32] text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${
                    plan.id === 'free' ? 'bg-gray-800' :
                    plan.id === 'pro' ? 'bg-[#32CD32]/10' :
                    'bg-yellow-400/10'
                  }`}>
                    <span className={getPlanColor(plan.id)}>
                      {getPlanIcon(plan.id)}
                    </span>
                  </div>
                  <CardTitle 
                    className={`text-2xl uppercase tracking-tight ${getPlanColor(plan.id)}`}
                    style={{ fontFamily: 'Barlow Condensed' }}
                  >
                    {plan.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">
                      {plan.price === 0 ? 'Grátis' : `€${plan.price.toFixed(2)}`}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-[#A1A1AA] text-sm">/mês</span>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="pt-6">
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${getPlanColor(plan.id)}`} />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    className={`w-full h-12 rounded-xl font-semibold ${
                      isCurrentPlan 
                        ? 'bg-gray-800 text-gray-400 cursor-not-allowed' 
                        : plan.id === 'free'
                          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          : plan.id === 'pro'
                            ? 'btn-primary'
                            : 'bg-yellow-400 text-black hover:bg-yellow-300'
                    }`}
                    disabled={isCurrentPlan || processingPlan === plan.id || plan.id === 'free'}
                    onClick={() => handleUpgrade(plan.id)}
                    data-testid={`upgrade-btn-${plan.id}`}
                  >
                    {processingPlan === plan.id ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        Processando...
                      </>
                    ) : isCurrentPlan ? (
                      <>
                        <CheckCircle2 className="mr-2 w-5 h-5" />
                        Plano Atual
                      </>
                    ) : plan.id === 'free' ? (
                      'Plano Básico'
                    ) : (
                      <>
                        <Crown className="mr-2 w-5 h-5" />
                        FAZER UPGRADE
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Features Comparison */}
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle 
              className="text-xl uppercase tracking-tight"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Comparação de Recursos
            </CardTitle>
            <CardDescription>
              Vê o que está incluído em cada plano
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="features-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-2 text-gray-400 font-medium">Recurso</th>
                    {plans.map((plan) => (
                      <th key={plan.id} className={`text-center py-4 px-2 font-medium ${getPlanColor(plan.id)}`}>
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-2 flex items-center gap-2 text-[#A1A1AA]">
                      <MessageSquare className="w-4 h-4 text-gray-500" />
                      Mensagens do Assistente
                    </td>
                    {plans.map((plan) => (
                      <td key={plan.id} className={`text-center py-4 px-2 ${getPlanColor(plan.id)}`} data-testid={`messages-limit-${plan.id}`}>
                        {plan.daily_messages === -1 ? 'Ilimitado' : `${plan.daily_messages}/dia`}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-2 flex items-center gap-2 text-[#A1A1AA]">
                      <Zap className="w-4 h-4 text-gray-500" />
                      Planos de Treino
                    </td>
                    {plans.map((plan) => (
                      <td key={plan.id} className={`text-center py-4 px-2 ${getPlanColor(plan.id)}`} data-testid={`plans-limit-${plan.id}`}>
                        {plan.monthly_plans === -1 ? 'Ilimitado' : `${plan.monthly_plans}/mês`}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-2 flex items-center gap-2 text-[#A1A1AA]">
                      <Star className="w-4 h-4 text-gray-500" />
                      Coach Virtual
                    </td>
                    <td className="text-center py-4 px-2"><XCircle className="w-5 h-5 text-gray-600 mx-auto" /></td>
                    <td className="text-center py-4 px-2"><Check className="w-5 h-5 text-[#32CD32] mx-auto" /></td>
                    <td className="text-center py-4 px-2"><Check className="w-5 h-5 text-yellow-400 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-2 flex items-center gap-2 text-[#A1A1AA]">
                      <TrendingUp className="w-4 h-4 text-gray-500" />
                      Dicas Personalizadas
                    </td>
                    <td className="text-center py-4 px-2"><XCircle className="w-5 h-5 text-gray-600 mx-auto" /></td>
                    <td className="text-center py-4 px-2"><Check className="w-5 h-5 text-[#32CD32] mx-auto" /></td>
                    <td className="text-center py-4 px-2"><Check className="w-5 h-5 text-yellow-400 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-4 px-2 flex items-center gap-2 text-[#A1A1AA]">
                      <Crown className="w-4 h-4 text-gray-500" />
                      Ajuste de Planos
                    </td>
                    <td className="text-center py-4 px-2"><XCircle className="w-5 h-5 text-gray-600 mx-auto" /></td>
                    <td className="text-center py-4 px-2"><XCircle className="w-5 h-5 text-gray-600 mx-auto" /></td>
                    <td className="text-center py-4 px-2"><Check className="w-5 h-5 text-yellow-400 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="py-4 px-2 flex items-center gap-2 text-[#A1A1AA]">
                      <Headphones className="w-4 h-4 text-gray-500" />
                      Suporte Prioritário
                    </td>
                    <td className="text-center py-4 px-2"><XCircle className="w-5 h-5 text-gray-600 mx-auto" /></td>
                    <td className="text-center py-4 px-2"><XCircle className="w-5 h-5 text-gray-600 mx-auto" /></td>
                    <td className="text-center py-4 px-2"><Check className="w-5 h-5 text-yellow-400 mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Usage Stats */}
        {userPlan && (
          <Card className="bg-[#121212] border-white/10 mt-6">
            <CardHeader>
              <CardTitle 
                className="text-lg uppercase tracking-tight"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Uso Atual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                      <p className="text-[#A1A1AA] text-sm">Mensagens do Assistente Hoje</p>
                  <p className="text-2xl font-bold">
                    {userPlan.daily_messages_used} 
                    <span className="text-gray-500 text-base font-normal">
                      / {userPlan.daily_messages_limit === -1 ? '∞' : userPlan.daily_messages_limit}
                    </span>
                  </p>
                </div>
                {userPlan.daily_messages_limit !== -1 && (
                  <div className="w-32 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#32CD32] rounded-full transition-all"
                      style={{ 
                        width: `${Math.min((userPlan.daily_messages_used / userPlan.daily_messages_limit) * 100, 100)}%` 
                      }}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PricingPage;
